/**
 * EarlyWarningSignalsPage - Main orchestrator component
 *
 * Root component for the Early Warning Signals dashboard.
 * Manages state for tabs, filters, pagination, search, and drawer.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { RefreshCw, ChevronRight } from 'lucide-react';
import { TabSwitcher, DrawerProvider } from 'equal-ds-ui';
import { ReportDownloadDropdown } from '../../ui/ReportDownloadDropdown';
import { envService } from '../../../services/env.service';
import {
  EarlyWarningSignalsPageProps,
  EWSTab,
  SearchDomain,
  EWSCustomer,
  EWSOverviewMetrics,
  EWSDistributionsData,
  PaginationInfo,
  DistributionRiskFilter,
  RiskLevel,
  BarClickContext,
  CustomerSortColumn,
  SortOrder,
  GetDistributionsParams,
  BalanceFilter,
  EWSView,
} from '../../../types/ews.types';
import { useEWSConfig } from '../../../hooks/useEWSConfig';
import { ewsApiService } from '../../../services/ews-api.service';
import { Select } from '../../ui/Select';
import { downloadAPI } from '../../../services/download-api.service';
import { DEFAULT_PAGE_SIZE } from '../../../constants/pagination.constants';
import { OverviewTab } from './OverviewTab';
import { CustomersTab } from './CustomersTab';
import { EWSCustomerDrawer } from './EWSCustomerDrawer';
import { EWSIndicatorCustomersDrawer } from './EWSIndicatorCustomersDrawer';

/**
 * Format timestamp to display format.
 */
function formatTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(',', '');
  } catch {
    return isoString;
  }
}

export const EarlyWarningSignalsPage: React.FC<EarlyWarningSignalsPageProps> = ({
  routeParams: _routeParams,
  queryParams,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<EWSTab>('overview');

  // The effective batchId: from query params or null (server uses default).
  // Hoisted above useEWSConfig so the hook can scope its fetch by batch.
  const effectiveBatchId = queryParams?.batch_id || undefined;
  const referenceId = queryParams?.reference_id || undefined;

  // View filter — drives WHERE filter on overview/customers/distributions queries +
  // which set of indicators appears in the customer drawer and analytics chips.
  // Backed by ews_is_salaried on parquet. The set of valid views + their order is
  // DB-sourced via useEWSConfig (ews_view_config table → /user/info piggyback).
  // We start as `null` and seed the first value from config once it loads, so the
  // FE never assumes a key the DB might not have.
  //
  // Pass batch_id so useEWSConfig picks up any per-batch overrides via the
  // 3-level (tenant, batch) resolution hierarchy. Without a batch, FAS returns
  // the tenant default.
  const { config: ewsConfig, loading: ewsConfigLoading, error: ewsConfigError, retry: retryEWSConfig } = useEWSConfig(effectiveBatchId);
  const [view, setView] = useState<EWSView | null>(null);

  // Seed `view` from the first config-defined view once config arrives. The Select
  // options come from the same source, so initial selection always matches what
  // the dropdown will show — no risk of FAS rejecting an unknown view key.
  useEffect(() => {
    if (view === null && ewsConfig && ewsConfig.views.length > 0) {
      setView(ewsConfig.views[0].value as EWSView);
    }
  }, [ewsConfig, view]);

  // Overview state
  const [metrics, setMetrics] = useState<EWSOverviewMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  // The view actually sent to the API. Pre-cutover batches have no populated
  // ews_is_salaried, so they can only be queried as "all_users" (the SQL builder
  // treats that as no salaried predicate); post-cutover batches use the selected
  // view. Local UI state (`view`) keeps the dropdown's selection across reloads.
  // Plain derived const — the result is a primitive, so useMemo would add overhead
  // with no referential-stability benefit.
  const effectiveView: EWSView | undefined =
    metrics?.hasIsSalariedData === false ? 'all_users' : (view ?? undefined);

  // Pre-cutover batches (no populated ews_is_salaried) can only be viewed as
  // "all_users". If we land on / switch to such a batch while a salaried or
  // self_employed view is selected, snap back to all_users so the dropdown's
  // value stays valid against its (narrowed) option list.
  useEffect(() => {
    if (metrics?.hasIsSalariedData === false && view && view !== 'all_users') {
      setView('all_users');
    }
  }, [metrics?.hasIsSalariedData, view]);

  // Distributions state (for analytics section)
  const [distributions, setDistributions] = useState<EWSDistributionsData | null>(null);
  const [isLoadingDistributions, setIsLoadingDistributions] = useState(true);
  const [distributionRiskFilter, setDistributionRiskFilter] = useState<DistributionRiskFilter>('ALL');

  // Customers state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState<SearchDomain>('consent_id');
  const [customers, setCustomers] = useState<EWSCustomer[] | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);

  // Sort state (for server-side sorting)
  const [sortBy, setSortBy] = useState<CustomerSortColumn | null>(null);

  // CSV meta columns discovered from the batch's csv_meta JSON
  const [metaColumns, setMetaColumns] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    // Balance filter state
    const [balanceFilter, setBalanceFilter] = useState<BalanceFilter | null>(null);

    // Risk level filter state
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | null>(null);

  // Customer detail drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<EWSCustomer | null>(null);
  const [isLoadingCustomerDetail, setIsLoadingCustomerDetail] = useState(false);

  // Cache for customer details to avoid redundant API calls
  const customerDetailCache = useRef<Map<string, EWSCustomer>>(new Map());

  // Ref for download dropdown panel width
  const pageContentRef = useRef<HTMLDivElement>(null);

  // Download button visibility from DB config (userinfo.ui_config.common) with envService fallback
  const showDownloadButton = useMemo(() => {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    const userInfo = encoded ? JSON.parse(atob(encoded)) : {};
    return userInfo?.ui_config?.common?.show_download_button_ews ?? envService.getUIConfig().common.show_download_button_ews;
  }, []);

  // Indicator customers drawer state (for bar click)
  const [isIndicatorDrawerOpen, setIsIndicatorDrawerOpen] = useState(false);
  const [indicatorContext, setIndicatorContext] = useState<BarClickContext | null>(null);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Clear customer detail cache when batch OR view changes — same customer fetched under
  // a different view returns a different set of risk factors, so old entries are stale.
  useEffect(() => {
    customerDetailCache.current.clear();
  }, [effectiveBatchId, view]);

  /**
   * Load overview metrics.
   */
  const loadMetrics = useCallback(async () => {
    if (!view) return;
    setIsLoadingMetrics(true);
    try {
      const data = await ewsApiService.getOverviewMetrics({
        batchId: effectiveBatchId,
        view: effectiveView,
      });
      setMetrics(data);
    } catch (error) {
      console.error('[EWS] Failed to load metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [effectiveBatchId, view, effectiveView]);

  /**
   * Load distribution data for analytics charts. No-ops until `view` has been seeded.
   */
  const loadDistributions = useCallback(async () => {
    if (!view) return;
    setIsLoadingDistributions(true);
    try {
      const params: GetDistributionsParams = {
        batchId: effectiveBatchId,
        view: effectiveView,
      };
      // Only pass riskLevel if not ALL
      if (distributionRiskFilter !== 'ALL') {
        params.riskLevel = distributionRiskFilter;
      }
      const data = await ewsApiService.getDistributions(params);
      setDistributions(data);
    } catch (error) {
      console.error('[EWS] Failed to load distributions:', error);
    } finally {
      setIsLoadingDistributions(false);
    }
  }, [effectiveBatchId, distributionRiskFilter, view, effectiveView]);

  /**
   * Load customers list.
   * Note: API only supports searchQuery for consent ID search, not different search domains.
   */
  const loadCustomers = useCallback(async () => {
    if (!view) return;
    setIsLoadingCustomers(true);
    try {
      const response = await ewsApiService.getCustomers({
        page: currentPage,
        pageSize,
        batchId: effectiveBatchId,
        searchQuery: searchQuery || undefined,
        searchDomain: searchQuery ? searchDomain : undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : undefined,
        balanceOperator: balanceFilter?.operator,
        balanceValue: balanceFilter?.value,
        riskLevel: riskLevelFilter || undefined,
        view: effectiveView,
      });
      setCustomers(response.data);
      setPagination(response.pagination);
      if (response.metaColumns && response.metaColumns.length > 0) {
        setMetaColumns(response.metaColumns);
      }
    } catch (error) {
      console.error('[EWS] Failed to load customers:', error);
    } finally {
      setIsLoadingCustomers(false);
    }
  }, [currentPage, pageSize, searchQuery, searchDomain, effectiveBatchId, sortBy, sortOrder, balanceFilter, riskLevelFilter, view, effectiveView]);

  /**
   * Handle refresh button click.
   */
  const handleRefresh = async () => {
    setIsRefreshing(true);
    customerDetailCache.current.clear();
    try {
      const data = await ewsApiService.refreshMetrics(effectiveBatchId);
      setMetrics(data);
      // Also refresh distributions
      await loadDistributions();
      if (activeTab === 'customers') {
        await loadCustomers();
      }
    } catch (error) {
      console.error('[EWS] Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Handle distribution risk filter change.
   */
  const handleDistributionRiskFilterChange = (filter: DistributionRiskFilter) => {
    setDistributionRiskFilter(filter);
  };

  /**
   * Handle search.
   * Only reset to page 1 if the query actually changed.
   */
  const handleSearch = useCallback((query: string, domain: SearchDomain) => {
    setSearchQuery(prevQuery => {
      // Only reset page if query actually changed
      if (prevQuery !== query) {
        setCurrentPage(1);
      }
      return query;
    });
    setSearchDomain(domain);
  }, []);

  /**
   * Handle page change.
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Handle page size change.
   */
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  /**
   * Handle sort change (server-side sorting).
   */
  const handleSortChange = (column: CustomerSortColumn | null, order: SortOrder) => {
    setSortBy(column);
    setSortOrder(order);
    setCurrentPage(1);
  };

  /**
   * Handle balance filter change.
   */
  const handleBalanceFilterChange = (filter: BalanceFilter | null) => {
    setBalanceFilter(filter);
    setCurrentPage(1);
  };

  /**
   * Handle risk level filter change.
   */
  const handleRiskLevelFilterChange = (level: RiskLevel | null) => {
    setRiskLevelFilter(level);
    setCurrentPage(1);
  };

  /**
   * Handle view toggle change. Batches view + page reset + filter clears in the
   * same event-handler pass so React 18 auto-batching collapses all setters into
   * one re-render — downstream loadCustomers fires exactly once with the new
   * view, page=1, and no stale balance/risk filters carried over from the
   * previous view (they're surfaced as chips below the table and a user switching
   * views expects a clean slate).
   */
  const handleViewChange = useCallback((nextView: EWSView) => {
    setView(nextView);
    setCurrentPage(1);
    setBalanceFilter(null);
    setRiskLevelFilter(null);
  }, []);

  /**
   * Handle customer row click.
   * Fetches full customer details from the API since list endpoint returns limited fields.
   */
  const handleCustomerClick = async (customer: EWSCustomer) => {
    const trackingId = customer.trackingId || customer.consentId;
    const cacheKey = trackingId ? `${trackingId}_${effectiveBatchId || ''}` : '';

    // Check cache first
    if (cacheKey && customerDetailCache.current.has(cacheKey)) {
      setSelectedCustomer(customerDetailCache.current.get(cacheKey)!);
      setIsDrawerOpen(true);
      return;
    }

    // Open drawer immediately with basic info from list
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
    setIsLoadingCustomerDetail(true);

    try {
      if (trackingId) {
        const details = await ewsApiService.getCustomerDetails(trackingId, effectiveBatchId, effectiveView);
        if (details) {
          setSelectedCustomer(details);
          customerDetailCache.current.set(cacheKey, details);
        }
      }
    } catch (error) {
      console.error('[EWS] Failed to load customer details:', error);
    } finally {
      setIsLoadingCustomerDetail(false);
    }
  };

  /**
   * Handle customer detail drawer close.
   * Also clears indicator context so back button won't appear incorrectly next time.
   */
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedCustomer(null);
    setIndicatorContext(null); // Clear indicator context on full close
  };

  /**
   * Handle bar click in analytics charts.
   * Opens the indicator customers drawer.
   */
  const handleBarClick = (
    indicator: string,
    indicatorName: string,
    level: RiskLevel,
    levelLabel: string,
    count: number
  ) => {
    setIndicatorContext({ indicator, indicatorName, level, levelLabel, count });
    setIsIndicatorDrawerOpen(true);
  };

  /**
   * Handle indicator drawer close.
   */
  const handleIndicatorDrawerClose = () => {
    setIsIndicatorDrawerOpen(false);
    setIndicatorContext(null);
  };

  /**
   * Handle customer click in indicator drawer.
   * Closes indicator drawer and opens customer detail drawer.
   * Preserves indicator context so user can go back.
   */
  const handleIndicatorCustomerClick = async (customer: EWSCustomer) => {
    const trackingId = customer.trackingId || customer.consentId;
    const cacheKey = trackingId ? `${trackingId}_${effectiveBatchId || ''}` : '';

    // Close indicator drawer first (but keep indicatorContext for back navigation)
    setIsIndicatorDrawerOpen(false);

    // Check cache first
    if (cacheKey && customerDetailCache.current.has(cacheKey)) {
      setSelectedCustomer(customerDetailCache.current.get(cacheKey)!);
      setIsDrawerOpen(true);
      return;
    }

    // Then open customer detail drawer
    setSelectedCustomer(customer);
    setIsDrawerOpen(true);
    setIsLoadingCustomerDetail(true);

    try {
      if (trackingId) {
        const details = await ewsApiService.getCustomerDetails(trackingId, effectiveBatchId, effectiveView);
        if (details) {
          setSelectedCustomer(details);
          customerDetailCache.current.set(cacheKey, details);
        }
      }
    } catch (error) {
      console.error('[EWS] Failed to load customer details:', error);
    } finally {
      setIsLoadingCustomerDetail(false);
    }
  };

  /**
   * Handle back from customer detail to indicator drawer.
   */
  const handleBackToIndicatorDrawer = () => {
    // Close customer drawer
    setIsDrawerOpen(false);
    setSelectedCustomer(null);
    // Reopen indicator drawer
    setIsIndicatorDrawerOpen(true);
  };

  /**
   * Handle tab change.
   */
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as EWSTab);
  };


  // Load metrics and distributions
  useEffect(() => {
    loadMetrics();
    loadDistributions();
  }, [loadMetrics, loadDistributions]);

  // Load customers when tab becomes active or filters change
  useEffect(() => {
    if (activeTab === 'customers') {
      loadCustomers();
    }
  }, [activeTab, loadCustomers]);

  // Dashboard view
  return (
    <DrawerProvider side="right" open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <div ref={pageContentRef} className="h-full flex flex-col">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
                  Early Warning Signals
                </h1>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-1.5 hover:text-gray-600 transition-colors disabled:opacity-50"
                  style={{ color: '#6b7280' }}
                  title="Refresh data"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
                {metrics?.lastRefreshedAt && (
                  <span className="text-xs" style={{ color: '#6b7280' }}>
                    (Last refreshed on {formatTimestamp(metrics.lastRefreshedAt)})
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-text-secondary mt-1">
              View/manage different user cohorts
            </p>
          </div>

          {/* Breadcrumb + Download */}
          <div className="flex items-center justify-between">
            <nav style={{ display: 'flex', alignItems: 'center', fontSize: '14px', gap: '6px' }}>
              <span
                onClick={() => { window.location.hash = '#/dashboard/bulkDataFetch?type=loan_monitoring'; }}
                style={{ color: '#6b7280', cursor: 'pointer' }}
              >
                Batches
              </span>
              {(referenceId || effectiveBatchId) && (
                <>
                  <ChevronRight style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                  <span style={{ color: '#111827', fontWeight: 600 }}>
                    {(() => {
                      const label = referenceId || effectiveBatchId || '';
                      return label.length > 24 ? `${label.slice(0, 24)}...` : label;
                    })()}
                  </span>
                </>
              )}
            </nav>

            {showDownloadButton && (
              <ReportDownloadDropdown
                buttonLabel="Download Data"
                buttonVariant="primary"
                reportTypes={[
                  { value: 'ews', title: 'EWS Report', description: 'Single report with balance information for all records' },
                  { value: 'raw', title: 'Raw Data', description: 'Single report with balance information for all records' },
                  // ponytail: analytics radio hidden for now — re-enable when analytics artifact is offered here
                  // { value: 'analytics', title: 'Analytics', description: 'Single report with balance information for all records' },
                ]}
                defaultReportType="ews"
                formats={[{ value: 'csv', label: '.csv' }]}
                defaultFormat="csv"
                onGenerate={async ({ reportType, format }) => {
                  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';
                  if (isJobBasedDownloadEnabled && effectiveBatchId) {
                    const downloadTypeMap: Record<string, string> = {
                      ews: 'EWS_REPORT',
                      raw: 'FI_RAW_DATA',
                      analytics: 'EWS_ANALYTICS',
                    };
                    const downloadType = downloadTypeMap[reportType] ?? 'EWS_REPORT';
                    await downloadAPI.createBatchJob(effectiveBatchId, downloadType);
                    return null;
                  }
                  return ewsApiService.exportReport({
                    period: 'lastYear', // Backend requires period param; time-period UI was removed, defaulting to 'lastYear' for full dataset
                    reportType: reportType as 'ews' | 'raw',
                    format: format as 'csv',
                    batchId: effectiveBatchId,
                  });
                }}
                getFileName={({ reportType, format }) => {
                  const labelMap: Record<string, string> = { ews: 'ews-report', raw: 'raw-data', analytics: 'banking-analysis' };
                  const label = labelMap[reportType] ?? reportType;
                  const suffix = effectiveBatchId ? effectiveBatchId.slice(0, 8) : 'all';
                  return `${label}-${suffix}.${format}`;
                }}
                postGenerateAction={{
                  label: 'GO TO DOWNLOAD CENTER',
                  onClick: () => {
                    if ((window as any).AngularAuthBridge?.navigate) {
                      (window as any).AngularAuthBridge.navigate('/dashboard/download-centre');
                    } else {
                      window.location.href = '/dashboard/download-centre';
                    }
                  },
                }}
                panelContainerRef={pageContentRef}
              />
            )}
          </div>

          {/* View filter dropdown — scopes both the Overview and Customers tabs. The
             selected value AND the option list come from the DB-driven config; initial
             selection is the first config entry, so the dropdown can never offer a key
             the FE doesn't already know is valid.

             Shown for every batch once overview returns. Pre-cutover batches (all-NULL
             ews_is_salaried, signalled by overview.hasIsSalariedData === false) can't be
             segmented by salaried/self-employed, so the option list is narrowed to
             "All Users" only; cutover-ready batches show all configured views. */}
          {metrics && (
            <div className="mb-3 w-[260px]">
              {ewsConfigError ? (
                <div className="flex items-center gap-2 text-sm text-red-600 px-2 py-1 border border-red-200 rounded">
                  <span className="flex-1">EWS config unavailable</span>
                  <button
                    type="button"
                    onClick={retryEWSConfig}
                    className="text-xs font-medium text-red-700 hover:text-red-800 underline underline-offset-2"
                  >
                    Retry
                  </button>
                </div>
              ) : ewsConfigLoading || !ewsConfig || !view ? (
                <div className="text-sm text-gray-500 px-2 py-1 border border-gray-200 rounded">
                  Loading…
                </div>
              ) : (
                <Select
                  value={view}
                  options={(metrics?.hasIsSalariedData === false
                    ? ewsConfig.views.filter(v => v.value === 'all_users')
                    : ewsConfig.views
                  ).map(v => ({ value: v.value, label: v.label }))}
                  onChange={(v) => handleViewChange(v as EWSView)}
                />
              )}
            </div>
          )}

          {/* Tab Switcher - no wrapper background */}
          <TabSwitcher
            tabs={[
              { id: 'overview', label: 'Overview' },
              { id: 'customers', label: 'Customers' },
            ]}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            variant="horizontal"
          />

          {/* Tab Content - flex-1 to fill remaining space */}
          <div className="flex-1 min-h-0 flex flex-col">
            {activeTab === 'overview' ? (
              <OverviewTab
                metrics={metrics}
                isLoading={isLoadingMetrics}
                distributions={distributions}
                isLoadingDistributions={isLoadingDistributions}
                selectedRiskFilter={distributionRiskFilter}
                onRiskFilterChange={handleDistributionRiskFilterChange}
                onBarClick={handleBarClick}
              />
            ) : (
              <CustomersTab
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                searchQuery={searchQuery}
                searchDomain={searchDomain}
                onSearch={handleSearch}
                customers={customers}
                pagination={pagination}
                isLoading={isLoadingCustomers}
                onCustomerClick={handleCustomerClick}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                enabledMetaColumns={metaColumns}
                metaColumns={metaColumns}
                balanceFilter={balanceFilter}
                onBalanceFilterChange={handleBalanceFilterChange}
                riskLevelFilter={riskLevelFilter}
                onRiskLevelFilterChange={handleRiskLevelFilterChange}
              />
            )}
          </div>
        </div>

        {/* Customer Detail Drawer */}
        <EWSCustomerDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          customer={selectedCustomer}
          isLoading={isLoadingCustomerDetail}
          onBack={indicatorContext ? handleBackToIndicatorDrawer : undefined}
        />

        {/* Indicator Customers Drawer (opened from bar click) */}
        {indicatorContext && (
          <EWSIndicatorCustomersDrawer
            isOpen={isIndicatorDrawerOpen}
            onClose={handleIndicatorDrawerClose}
            indicator={indicatorContext.indicator}
            indicatorName={indicatorContext.indicatorName}
            level={indicatorContext.level}
            levelLabel={indicatorContext.levelLabel}
            totalCount={indicatorContext.count}
            batchId={effectiveBatchId}
            onCustomerClick={handleIndicatorCustomerClick}
          />
        )}
      </div>
    </DrawerProvider>
  );
};

export default EarlyWarningSignalsPage;
